/**
 * Intranet Osasco Plaza — src/modules/servicos.jsx
 * Telas do módulo "Solicitações de Serviço". Extraído de app.jsx — ver
 * DOC/ADR/0011.
 */

import { Icon, StatusPill, TimeSelect, calcMaxDate, calcMinDate, fmtBytes, fmtDateBR, fmtDateInput, hasPerm, iconeAnexo, maskCpf, maskPhone, relativeTime, serviceTypes, validarCPF } from './shared.jsx';
const { useState, useEffect, useRef } = React;


export function NewRequest({ go, mobile, onSubmit, onDone, user }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    type:'', title:'', desc:'', priority:'Normal',
    location:'',
    periodoDeData:'', periodoDeHora:'',
    periodoAteData:'', periodoAteHora:'',
    cpf:'', nomeProfissional:'', nomeEmpresa:'', telefone:'',
  });
  const [cpfAutoFill, setCpfAutoFill] = useState(false);
  const [cpfErr, setCpfErr] = useState('');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);

  const ALLOWED_TYPES = ['image/png','image/jpeg','image/jpg','application/pdf'];
  const MAX_SIZE_MB = 25;
  const MAX_FILES = 10;

  const addFiles = (incoming) => {
    const valid = Array.from(incoming).filter(f => {
      if (!ALLOWED_TYPES.includes(f.type)) return false;
      if (f.size > MAX_SIZE_MB * 1024 * 1024) return false;
      return true;
    });
    setFiles(prev => {
      const merged = [...prev];
      for (const f of valid) {
        if (merged.length >= MAX_FILES) break;
        if (!merged.find(x => x.name === f.name && x.size === f.size)) merged.push(f);
      }
      return merged;
    });
  };
  const removeFile = (idx) => setFiles(prev => prev.filter((_,i) => i !== idx));
  const fmtSize = (bytes) => bytes < 1024*1024 ? (bytes/1024).toFixed(0)+'KB' : (bytes/(1024*1024)).toFixed(1)+'MB';

  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState('');

  const [progresso, setProgresso] = useState('');

  const enviar = async () => {
    setEnviando(true); setErroEnvio(''); setProgresso('');

    /* Dois passos, nesta ordem, porque o anexo precisa do id da solicitação:
       primeiro cria, depois envia os arquivos.

       A consequência é que o passo 2 pode falhar com o passo 1 já feito — e a
       solicitação existe, mesmo que nenhum anexo tenha subido. Falar isso na
       cara é melhor do que mostrar "erro ao enviar" e deixar a pessoa
       preencher tudo de novo, criando um chamado duplicado. */
    let criada;
    try {
      const { files: _fora, ...dados } = { ...data, files };
      criada = await onSubmit(dados);
    } catch (e) {
      setErroEnvio(e.message || 'Não foi possível enviar a solicitação.');
      setEnviando(false);
      return;
    }

    if (files.length && criada?.id) {
      const falhas = [];
      for (let i = 0; i < files.length; i++) {
        setProgresso(`Enviando anexo ${i + 1} de ${files.length}: ${files[i].name}`);
        try { await API.solicitacoes.enviarAnexo(criada.id, files[i]); }
        catch (e) { falhas.push(`${files[i].name} (${e.message})`); }
      }
      if (falhas.length) {
        setEnviando(false); setProgresso('');
        setErroEnvio(
          `A solicitação ${criada.id} foi criada, mas ${falhas.length} anexo(s) não subiram: ` +
          falhas.join('; ') + '. Abra a solicitação em "Meus pedidos" e anexe por lá.'
        );
        return;
      }
    }

    onDone && onDone();
  };

  const steps = [
    { label:'Tipo de serviço' },
    { label:'Detalhes' },
    { label:'Quem e Quando' },
    { label:'Revisão' },
  ];
  const next = () => setStep(s => Math.min(s+1, steps.length-1));
  const prev = () => setStep(s => Math.max(s-1, 0));
  const set  = (k,v) => setData(d => ({...d, [k]:v}));
  const typeLabel = (serviceTypes.find(t=>t.id===data.type)||{}).label||'—';

  const minDate = fmtDateInput(calcMinDate());

  const handleCpf = (raw) => {
    const fmt = maskCpf(raw);
    const digits = fmt.replace(/\D/g,'');
    set('cpf', fmt);
    if (digits.length === 11) {
      if (!validarCPF(digits)) { setCpfErr('CPF inválido. Verifique os dígitos.'); return; }
      setCpfErr('');
      /* Busca no servidor, dentro do escopo do usuário.

         Antes isto era feito no navegador comparando SHA-256 do CPF. Um CPF
         tem 11 dígitos: 10^11 combinações, revertidas por força bruta em
         minutos. Aquele "hash" dava sensação de proteção sem entregar
         nenhuma — e ainda exigia carregar todas as solicitações no cliente
         para comparar, o que por si só já vazava a base inteira. */
      API.solicitacoes.buscarPrestador(digits).then(r => {
        if (!r.encontrado) return;
        setData(d => ({
          ...d, cpf: fmt,
          nomeProfissional: r.prestador.nomeProfissional || d.nomeProfissional,
          nomeEmpresa:      r.prestador.nomeEmpresa      || d.nomeEmpresa,
          telefone:         r.prestador.telefone         || d.telefone,
        }));
        setCpfAutoFill(true);
        setTimeout(() => setCpfAutoFill(false), 3500);
      }).catch(() => { /* autopreenchimento é conveniência: falhou, digita à mão */ });
    } else {
      setCpfErr('');
    }
  };

  // ── Validação de prazo x prioridade ── (hooks sempre no topo, sem condicional)
  const [prioAlertConfirmed, setPrioAlertConfirmed] = useState(false);
  const prevPrioKeyRef = React.useRef('');

  // Valida datas do período
  const periodoAteOk = !data.periodoAteData || (
    data.periodoAteData >= data.periodoDeData &&
    data.periodoAteData <= fmtDateInput(calcMaxDate(new Date(data.periodoDeData || minDate)))
  );

  // ── Validação de prazo a partir do MOMENTO ATUAL do cadastro ──
  // Crítica (SLA 1h): "Período até" não pode ultrapassar agora + 1h
  // Alta    (SLA 8h): "Período até" não pode ultrapassar agora + 8h
  // A comparação é sempre de AGORA até o fim informado, não entre as duas datas.
  const prioAlert = (() => {
    if (!data.periodoAteData) return null;
    if (data.priority !== 'Crítica' && data.priority !== 'Alta') return null;

    const agora = new Date();
    const ateStr = data.periodoAteData + 'T' + (data.periodoAteHora || '23:59') + ':00';
    const ate = new Date(ateStr);
    const horasAteAte = (ate - agora) / (1000 * 60 * 60); // horas de agora até o fim

    // Formata label legível do fim informado
    const [ay, am, ad] = data.periodoAteData.split('-');
    const fimLabel = `${ad}/${am}/${ay}${data.periodoAteHora ? ' às ' + data.periodoAteHora : ''}`;

    if (data.priority === 'Crítica' && horasAteAte > 1) {
      const limiteStr = (() => {
        const lim = new Date(agora.getTime() + 60 * 60 * 1000);
        return lim.toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' });
      })();
      return {
        level: 'err',
        title: '⚠ Prazo incompatível com prioridade Crítica',
        body: `Prioridade Crítica exige atendimento em até 1 hora. O prazo máximo permitido agora é até ${limiteStr} de hoje. A data/hora informada (${fimLabel}) ultrapassa esse limite. Corrija o prazo ou altere a prioridade.`,
        needsConfirm: false,
      };
    }
    if (data.priority === 'Alta' && horasAteAte > 8) {
      const limiteStr = (() => {
        const lim = new Date(agora.getTime() + 8 * 60 * 60 * 1000);
        const [ld, lm, lh, lmin] = [
          String(lim.getDate()).padStart(2,'0'),
          String(lim.getMonth()+1).padStart(2,'0'),
          String(lim.getHours()).padStart(2,'0'),
          String(lim.getMinutes()).padStart(2,'0'),
        ];
        return `${ld}/${lm} às ${lh}:${lmin}`;
      })();
      return {
        level: 'warn',
        title: '⚠ Prazo estendido para prioridade Alta',
        body: `Prioridade Alta exige atendimento em até 8 horas. O prazo máximo seria até ${limiteStr}. A data/hora informada (${fimLabel}) ultrapassa esse limite. Confirme para prosseguir ou ajuste as datas / prioridade.`,
        needsConfirm: true,
      };
    }
    return null;
  })();

  // Reseta confirmação via useEffect quando datas, horas ou prioridade mudam
  const prioKey = data.priority + '|' + data.periodoAteData + '|' + data.periodoAteHora;
  React.useEffect(() => {
    if (prevPrioKeyRef.current && prevPrioKeyRef.current !== prioKey) {
      setPrioAlertConfirmed(false);
    }
    prevPrioKeyRef.current = prioKey;
  }, [prioKey]);

  // Bloqueia avanço se: Crítica fora do prazo (erro) OU Alta fora do prazo não confirmada
  const prioBlock = prioAlert && (prioAlert.level === 'err' || (prioAlert.needsConfirm && !prioAlertConfirmed));

  const step2ok = data.periodoDeData && periodoAteOk && validarCPF(data.cpf.replace(/\D/g,'')) && !cpfErr && data.nomeProfissional && data.telefone && !prioBlock;

  return (
    <div className="main-pad">
      <div className="page-title-row"><div className="page-title"><h2>Nova solicitação</h2><p>Self-service em 4 etapas · status em tempo real</p></div></div>
      <div className="stepper-shell">
        <aside className="stepper-aside">
          <h3>Solicitação</h3><h2>Conte o que precisa</h2>
          <div className="step-list">{steps.map((s,i)=>(
            <div key={i} className={`step-item ${i===step?'active':''} ${i<step?'done':''}`}>
              <div className="step-num">{i<step?<Icon name="check" size={12}/>:i+1}</div>
              <div className="step-label">{s.label}</div>
            </div>
          ))}</div>
          <div className="footer-tip">Roteamento automático · notificações em tempo real · auditoria criptografada.</div>
        </aside>

        <div className="stepper-main">

          {/* ── STEP 0 ── */}
          {step===0 && <div>
            <h3>Que tipo de serviço você precisa?</h3>
            <p className="lead">Selecione a categoria.</p>
            <div className="type-grid">{serviceTypes.map(t=>(
              <button key={t.id} className={`type-card ${data.type===t.id?'selected':''}`} onClick={()=>set('type',t.id)}>
                <div className="ti"><Icon name={t.icon} size={18}/></div>
                <div><div className="tl">{t.label}</div><div className="td">{t.desc}</div></div>
              </button>
            ))}</div>
          </div>}

          {/* ── STEP 1 ── */}
          {step===1 && <div>
            <h3>Detalhes da solicitação</h3>
            <p className="lead">Quanto mais claro, mais rápido a equipe resolve.</p>
            <div className="field"><label>Título <span className="req">*</span></label><input type="text" placeholder="Ex.: Troca de lâmpada queimada na vitrine" value={data.title} onChange={e=>set('title',e.target.value)}/></div>
            <div className="field"><label>Descrição</label><textarea rows={4} placeholder="Descreva o ocorrido..." value={data.desc} onChange={e=>set('desc',e.target.value)}/></div>
            <div className="field"><label>Prioridade percebida</label>
              <div className="priority-row">{['Muito Baixa','Baixa','Normal','Alta','Crítica'].map(p=>(
                <button key={p} data-p={p} className={`priority-card ${data.priority===p?'selected':''}`} onClick={()=>set('priority',p)}>
                  <div className="pl">{p}</div>
                  <div className="ps">{p==='Muito Baixa'?'+ de 7 dias':p==='Baixa'?'até 7 dias':p==='Normal'?'até 48h':p==='Alta'?'até 8h':'até 1h'}</div>
                </button>
              ))}</div>
            </div>
          </div>}

          {/* ── STEP 2 — Quem e Quando ── */}
          {step===2 && <div>
            <h3>Quem e Quando?</h3>
            <p className="lead">Informe o profissional e o período da solicitação.</p>

            {/* Loja/Setor vem do cadastro do usuário */}
            <div style={{ display:"flex", alignItems:"center", gap:10, background:"var(--info-bg)", border:"1px solid rgba(37,99,235,0.25)", borderRadius:8, padding:"10px 14px", marginBottom:18, fontSize:12, color:"var(--info)" }}>
              <Icon name="map-pin" size={14}/>
              <div><strong>Loja / Setor: </strong>{[user.store, user.setor].filter(Boolean).join(" · ") || "Não informado no cadastro"}</div>
            </div>
            {/* Período */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div className="field">
                <label>Período de <span className="req">*</span></label>
                <div style={{ display:'flex', gap:6 }}>
                  <input type="date" value={data.periodoDeData} min={minDate}
                    max={fmtDateInput(calcMaxDate(new Date(minDate)))}
                    style={{ flex:2 }}
                    onChange={e=>{
                      set('periodoDeData', e.target.value);
                      // se até < de, limpa até
                      if (data.periodoAteData && data.periodoAteData < e.target.value) set('periodoAteData','');
                    }}/>
                  <TimeSelect value={data.periodoDeHora} onChange={v=>set("periodoDeHora",v)}/>
                </div>
              </div>
              <div className="field">
                <label>Período até</label>
                <div style={{ display:'flex', gap:6 }}>
                  <input type="date" value={data.periodoAteData}
                    min={data.periodoDeData || minDate}
                    max={fmtDateInput(calcMaxDate(new Date(data.periodoDeData || minDate)))}
                    style={{ flex:2 }}
                    onChange={e => {
                      const val = e.target.value;
                      const maxAllowed = fmtDateInput(calcMaxDate(new Date(data.periodoDeData || minDate)));
                      // iOS/Safari ignora max — forçar via JS
                      if (val > maxAllowed) { set('periodoAteData', maxAllowed); }
                      else { set('periodoAteData', val); }
                    }}/>
                  <TimeSelect value={data.periodoAteHora} onChange={v=>set("periodoAteHora",v)}/>
                </div>
                {!periodoAteOk && <div className="hint" style={{ color:'var(--err)' }}>Máximo 15 dias a partir da data de início.</div>}
              </div>
            </div>

            {/* ── Banner de alerta: prioridade x prazo ── */}
            {prioAlert && (
              <div style={{
                display:'flex', flexDirection:'column', gap:8,
                background: prioAlert.level==='err' ? 'var(--err-bg)' : 'var(--warn-bg)',
                border: `1.5px solid ${prioAlert.level==='err' ? 'var(--err)' : 'var(--warn)'}`,
                borderRadius:10, padding:'14px 16px', marginTop:4, marginBottom:4,
                fontSize:13, color: prioAlert.level==='err' ? 'var(--err)' : 'var(--warn)',
              }}>
                <div style={{ fontWeight:700, fontSize:13 }}>{prioAlert.title}</div>
                <div style={{ fontWeight:400, lineHeight:1.6, marginTop:2, color: prioAlert.level==='err' ? '#7f1d1d' : '#78350f' }}>{prioAlert.body}</div>
                {prioAlert.needsConfirm && !prioAlertConfirmed && (
                  <div style={{ marginTop:8, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                    <button className="btn btn-sm" onClick={()=>setPrioAlertConfirmed(true)}
                      style={{ background:'var(--warn)', color:'white', borderColor:'var(--warn)', fontWeight:600 }}>
                      Sim, confirmar mesmo assim
                    </button>
                    <span style={{ fontSize:11, color:'#92400e' }}>Ou ajuste as datas / prioridade acima.</span>
                  </div>
                )}
                {prioAlert.needsConfirm && prioAlertConfirmed && (
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4, fontWeight:600, color:'var(--ok)', fontSize:12 }}>
                    <Icon name="check-circle" size={14}/> Confirmado — você pode continuar.
                  </div>
                )}
              </div>
            )}

            {/* Dados do profissional */}
            <div style={{ borderTop:'1px solid var(--line)', margin:'18px 0 16px', paddingTop:16 }}>
              <div className="label-eyebrow" style={{ marginBottom:12 }}>DADOS DO PROFISSIONAL</div>
            </div>

            {cpfAutoFill && (
              <div style={{ display:'flex', alignItems:'center', gap:8, background:'var(--ok-bg)', border:'1px solid var(--ok)', borderRadius:8, padding:'8px 12px', marginBottom:14, fontSize:12, color:'var(--ok)' }}>
                <Icon name="check-circle" size={14}/> CPF encontrado — nome e telefone preenchidos automaticamente.
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div className="field">
                <label>CPF <span className="req">*</span></label>
                <input type="text" placeholder="000.000.000-00" value={data.cpf} onChange={e=>handleCpf(e.target.value)} maxLength={14}
                  style={{ borderColor: cpfErr ? 'var(--err)' : (data.cpf.replace(/\D/g,'').length===11 && !cpfErr ? 'var(--ok)' : undefined) }}/>
                {cpfErr && <div className="hint" style={{color:'var(--err)'}}>⚠ {cpfErr}</div>}
                {!cpfErr && data.cpf.replace(/\D/g,'').length===11 && <div className="hint" style={{color:'var(--ok)'}}>✓ CPF válido</div>}
              </div>
              <div className="field">
                <label>Nome do Profissional <span className="req">*</span></label>
                <input type="text" placeholder="Nome completo" value={data.nomeProfissional} onChange={e=>set('nomeProfissional',e.target.value)}/>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div className="field">
                <label>Nome da Empresa</label>
                <input type="text" placeholder="Razão social ou nome fantasia" value={data.nomeEmpresa} onChange={e=>set('nomeEmpresa',e.target.value)}/>
              </div>
              <div className="field">
                <label>Telefone <span className="req">*</span></label>
                <input type="text" placeholder="(XX) XXXXX-XXXX" value={data.telefone} onChange={e=>set('telefone', maskPhone(e.target.value))} maxLength={15}/>
              </div>
            </div>
          </div>}

          {/* ── STEP 3 ── */}
          {step===3 && <div>
            <h3>Revisão final</h3>
            <p className="lead">Confirme os dados antes de enviar.</p>
            {/* ── Upload de arquivos ──
                Desde a Fase 1.1 os arquivos sobem de verdade, para o R2, logo
                depois que a solicitação é criada (o anexo precisa do id dela).
                Os limites abaixo são conferidos aqui só para dar retorno
                imediato — quem decide é o Worker, que ainda checa os magic
                bytes do arquivo e ignora o tipo declarado. */}
            <input ref={fileInputRef} type="file" multiple
              accept=".png,.jpg,.jpeg,.pdf"
              style={{ display:'none' }}
              onChange={e=>{ addFiles(e.target.files); e.target.value=''; }}/>

            <div className={`upload-zone${dragOver?' upload-zone-active':''}`}
              onClick={()=>fileInputRef.current.click()}
              onDragOver={e=>{ e.preventDefault(); setDragOver(true); }}
              onDragLeave={()=>setDragOver(false)}
              onDrop={e=>{ e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}>
              <Icon name="paperclip" size={22} style={{ color: dragOver ? 'var(--navy)' : 'var(--ink-3)' }}/>
              <div className="uh">{dragOver ? 'Solte para anexar' : 'Arraste arquivos ou clique para selecionar'}</div>
              <div className="us">PNG, JPG, PDF · até 25 MB por arquivo · máx. 10 arquivos</div>
            </div>

            {files.length > 0 && (
              <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:6 }}>
                {files.map((f,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10, background:'var(--surface-2)', border:'1px solid var(--line)', borderRadius:8, padding:'8px 12px', fontSize:12 }}>
                    <Icon name="paperclip" size={14} style={{ color:'var(--ink-3)', flexShrink:0 }}/>
                    <span style={{ flex:1, color:'var(--ink)', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
                    <span style={{ color:'var(--ink-4)', flexShrink:0 }}>{fmtSize(f.size)}</span>
                    <button onClick={e=>{ e.stopPropagation(); removeFile(i); }}
                      style={{ background:'transparent', border:'none', cursor:'pointer', color:'var(--ink-4)', padding:'2px 4px', borderRadius:4, display:'flex', alignItems:'center' }}
                      title="Remover arquivo">
                      <Icon name="x" size={13}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="divider"/>
            <div className="label-eyebrow">SERVIÇO</div>
            <div className="review-row"><span className="k">Tipo</span><span className="v">{typeLabel}</span></div>
            <div className="review-row"><span className="k">Título</span><span className="v">{data.title||'—'}</span></div>
            <div className="review-row"><span className="k">Prioridade</span><span className="v">{data.priority}</span></div>
            <div className="divider"/>
            <div className="label-eyebrow">LOCAL E PERÍODO</div>
            <div className="review-row"><span className="k">Localização</span><span className="v">{[user.store, user.setor].filter(Boolean).join(" · ") || "—"}</span></div>
            <div className="review-row"><span className="k">Período de</span><span className="v">{fmtDateBR(data.periodoDeData)}{data.periodoDeHora ? ' às '+data.periodoDeHora : ''}</span></div>
            <div className="review-row"><span className="k">Período até</span><span className="v">{data.periodoAteData ? fmtDateBR(data.periodoAteData)+(data.periodoAteHora?' às '+data.periodoAteHora:'') : '—'}</span></div>
            <div className="divider"/>
            <div className="label-eyebrow">PROFISSIONAL</div>
            <div className="review-row"><span className="k">CPF</span><span className="v">{data.cpf ? '***.***.***-'+data.cpf.slice(-2) : '—'}</span></div>
            <div className="review-row"><span className="k">Nome</span><span className="v">{data.nomeProfissional||'—'}</span></div>
            <div className="review-row"><span className="k">Empresa</span><span className="v">{data.nomeEmpresa||'—'}</span></div>
            <div className="review-row"><span className="k">Telefone</span><span className="v">{data.telefone||'—'}</span></div>
          </div>}

          {progresso && (
            <div style={{ margin:'0 0 12px',padding:'10px 14px',background:'var(--info-bg)',border:'1px solid #c7d6f5',borderRadius:8,color:'var(--info)',fontSize:13 }}>
              {progresso}
            </div>
          )}
          {erroEnvio && (
            <div style={{ margin:'0 0 12px',padding:'10px 14px',background:'var(--err-bg)',border:'1px solid #f5c2c2',borderRadius:8,color:'var(--err)',fontSize:13,lineHeight:1.6 }}>
              {erroEnvio}
            </div>
          )}
          <div className="stepper-foot">
            <button className="btn btn-ghost" onClick={step===0?()=>go('home'):prev}><Icon name="arrow-left" size={13}/> {step===0?'Cancelar':'Voltar'}</button>
            {step < steps.length-1
              ? <button className="btn btn-primary" onClick={next}
                  disabled={(step===0&&!data.type)||(step===1&&!data.title)||(step===2&&!step2ok)}>
                  Continuar <Icon name="arrow-right" size={13}/>
                </button>
              : <button className="btn btn-gold" onClick={enviar} disabled={enviando}>
                  <Icon name="check" size={13}/> {enviando ? 'Enviando...' : (files.length ? `Enviar com ${files.length} anexo${files.length>1?'s':''}` : 'Enviar solicitação')}
                </button>}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ── MY REQUESTS ── */
export function RequestActionModal({ req, action, onConfirm, onClose }) {
  const [obs, setObs] = useState('');
  const isReject = action === 'rejeitar';
  const color  = isReject ? 'var(--err)' : 'var(--ok)';
  const title  = isReject ? '❌ Reprovar solicitação' : action === 'aprovar' ? '✅ Aprovar solicitação' : '➡️ Avançar status';
  /* Espelha TRANSICOES do solicitacoes-worker. Aqui serve só para mostrar o
     próximo status na tela — quem decide se a transição vale é o servidor,
     que recusa com 409 o que não estiver na lista dele. Se os dois
     divergirem, o usuário vê um erro claro em vez de uma mudança indevida. */
  const TRANSICOES = {
    'Pendente':             ['Aguardando aprovação','Em andamento','Cancelado'],
    'Aguardando aprovação': ['Em andamento','Cancelado'],
    'Em andamento':         ['Concluído','Cancelado'],
    'Concluído':            [],
    'Cancelado':            [],
  };
  const nextStatus = () => {
    if (isReject) return 'Cancelado';
    if (action === 'aprovar') return 'Em andamento';
    const possiveis = (TRANSICOES[req.status] || []).filter(x => x !== 'Cancelado');
    return possiveis[0] || req.status;
  };
  const [enviando, setEnviando] = useState(false);
  const submit = async () => {
    if (isReject && !obs.trim()) return;
    setEnviando(true);
    /* `progress` não é mais enviado: o servidor o deriva do status. No
       protótipo ele vinha do cliente, então dava para marcar 100% numa
       solicitação recém-aberta. */
    await onConfirm(req.id, nextStatus(), null, obs.trim()||null);
    setEnviando(false);
    onClose();
  };
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-card" style={{ maxWidth:460 }}>
        <button className="close-x" onClick={onClose}><Icon name="x" size={16}/></button>
        <h3 style={{ color }}>{title}</h3>
        <div style={{ fontSize:12,color:'var(--ink-3)',marginBottom:16 }}>Solicitação <b>{req.id}</b> — {req.title}</div>
        <div className="divider"/>
        <div style={{ background:'var(--surface-2)',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12 }}>
          <div style={{ display:'flex',justifyContent:'space-between',marginBottom:4 }}>
            <span style={{ color:'var(--ink-3)' }}>Status atual</span><StatusPill status={req.status}/>
          </div>
          <div style={{ display:'flex',justifyContent:'space-between',marginTop:6 }}>
            <span style={{ color:'var(--ink-3)' }}>Novo status</span><StatusPill status={nextStatus()}/>
          </div>
        </div>
        <div className="field">
          <label>{isReject ? <span>Motivo <span className="req">*</span></span> : 'Observação (opcional)'}</label>
          <textarea rows={3} value={obs} onChange={e=>setObs(e.target.value)}
            placeholder={isReject ? 'Informe o motivo da reprovação...' : 'Observação opcional...'}/>
        </div>
        {isReject && !obs.trim() && <div style={{ fontSize:11,color:'var(--err)',marginBottom:8 }}>Motivo obrigatório para reprovar.</div>}
        <div style={{ display:'flex',gap:8,justifyContent:'flex-end',marginTop:8 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className={isReject ? 'btn btn-danger' : 'btn btn-primary'} onClick={submit} disabled={enviando||(isReject&&!obs.trim())}>
            {isReject ? <Icon name="x" size={13}/> : <Icon name="check" size={13}/>} {isReject ? 'Reprovar' : action==='aprovar' ? 'Aprovar' : 'Avançar'}
          </button>
        </div>
      </div>
    </div>
  );
}


export function RequestDetailModal({ id, user, onClose, onMudou }) {
  const [dados, setDados]   = useState(null);
  const [erro, setErro]     = useState('');
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando]     = useState('');
  const [acao, setAcao]     = useState(null);
  const inputRef = React.useRef(null);

  const carregar = async () => {
    setCarregando(true); setErro('');
    try { setDados(await API.solicitacoes.detalhe(id)); }
    catch (e) { setErro(e.message || 'Não foi possível carregar a solicitação.'); }
    setCarregando(false);
  };
  useEffect(() => { carregar(); }, [id]);

  const enviarArquivos = async (lista) => {
    const arquivos = Array.from(lista || []);
    if (!arquivos.length) return;
    setErro('');
    for (const f of arquivos) {
      setEnviando(f.name);
      try {
        await API.solicitacoes.enviarAnexo(id, f);
      } catch (e) {
        /* Para no primeiro erro em vez de seguir: se o limite estourou ou o
           formato foi recusado, insistir com os próximos só empilha erro. */
        setErro(`${f.name}: ${e.message}`);
        break;
      }
    }
    setEnviando('');
    await carregar();
    onMudou && onMudou();
  };

  const removerAnexo = async (anexoId, nome) => {
    if (!confirm(`Remover o anexo "${nome}"?`)) return;
    try { await API.solicitacoes.removerAnexo(id, anexoId); await carregar(); onMudou && onMudou(); }
    catch (e) { setErro(e.message || 'Não foi possível remover o anexo.'); }
  };

  const baixar = async (anexo) => {
    try { await API.solicitacoes.baixarAnexo(id, anexo.id, anexo.nome); }
    catch (e) { setErro(e.message || 'Não foi possível baixar o anexo.'); }
  };

  const confirmarAcao = async (solId, status, _prog, obs) => {
    try {
      await API.solicitacoes.mudarStatus(solId, status, obs);
      await carregar();
      onMudou && onMudou();
    } catch (e) { setErro(e.message || 'Não foi possível alterar o status.'); }
  };

  const s = dados?.solicitacao;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-card" style={{ maxWidth: 720, maxHeight: '88vh', overflowY: 'auto' }}>
        <button className="close-x" onClick={onClose}><Icon name="x" size={16}/></button>

        {carregando && <div style={{ padding: 30, color: 'var(--ink-3)', fontSize: 13 }}>Carregando…</div>}

        {erro && (
          <div style={{ margin: '0 0 14px', padding: '10px 14px', background: 'var(--err-bg)', border: '1px solid #f5c2c2', borderRadius: 8, color: 'var(--err)', fontSize: 13 }}>
            {erro}
          </div>
        )}

        {s && (<>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="req-detail-id">{s.id}</div>
              <h3 style={{ margin: '2px 0 0' }}>{s.title}</h3>
            </div>
            <StatusPill status={s.status}/>
          </div>

          <div className="req-detail-meta" style={{ marginBottom: 14 }}>
            <span><Icon name="briefcase" size={11}/> {s.owner}</span><span className="dot"/>
            <span><Icon name="user" size={11}/> {s.createdByNome}</span><span className="dot"/>
            <span><Icon name="clock" size={11}/> aberta {relativeTime(s.created)}</span>
          </div>

          {s.desc && <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '12px 14px', fontSize: 13, lineHeight: 1.6, marginBottom: 14, whiteSpace: 'pre-wrap' }}>{s.desc}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12, marginBottom: 16 }}>
            <div><span style={{ color: 'var(--ink-3)' }}>Prioridade:</span> <b style={{ color: s.priority === 'Crítica' ? 'var(--err)' : 'var(--ink)' }}>{s.priority}</b></div>
            {s.location && <div><span style={{ color: 'var(--ink-3)' }}>Local:</span> <b>{s.location}</b></div>}
            {s.periodoDe && <div><span style={{ color: 'var(--ink-3)' }}>Período:</span> <b>{s.periodoDe}{s.periodoAte ? ' → ' + s.periodoAte : ''}</b></div>}
          </div>

          {/* ── Prestador ── */}
          {s.temPrestador && (
            <div style={{ marginBottom: 16 }}>
              <div className="divider"/>
              <h4 style={{ fontSize: 13, margin: '12px 0 8px' }}>👷 Prestador</h4>
              {dados.prestadorVisivel ? (
                <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                  {s.nomeProfissional && <div><span style={{ color: 'var(--ink-3)' }}>Nome:</span> <b>{s.nomeProfissional}</b></div>}
                  {s.nomeEmpresa && <div><span style={{ color: 'var(--ink-3)' }}>Empresa:</span> <b>{s.nomeEmpresa}</b></div>}
                  {s.cpf && <div><span style={{ color: 'var(--ink-3)' }}>CPF:</span> <b style={{ fontFamily: "'JetBrains Mono',monospace" }}>{s.cpf}</b></div>}
                  {s.telefone && <div><span style={{ color: 'var(--ink-3)' }}>Telefone:</span> <b>{s.telefone}</b></div>}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                  Esta solicitação tem dados de prestador, mas eles só são visíveis para quem a abriu,
                  para o gestor da loja e para a administração.
                </div>
              )}
            </div>
          )}

          {/* ── Anexos ── */}
          <div className="divider"/>
          <h4 style={{ fontSize: 13, margin: '12px 0 8px' }}>📎 Anexos {dados.anexos.length ? `(${dados.anexos.length})` : ''}</h4>

          {dados.anexos.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>Nenhum arquivo anexado.</div>}

          {dados.anexos.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>{iconeAnexo(a.tipo)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nome}</div>
                <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{fmtBytes(a.tamanho)} · {a.enviadoPor} · {relativeTime(a.criadoEm)}</div>
              </div>
              <button className="btn btn-sm btn-ghost" onClick={() => baixar(a)}>Baixar</button>
              {(dados.podeAnexar || user.role === 'admin') &&
                <button className="btn btn-sm btn-ghost" style={{ color: 'var(--err)' }} onClick={() => removerAnexo(a.id, a.nome)}>
                  <Icon name="x" size={12}/>
                </button>}
            </div>
          ))}

          {dados.podeAnexar && (
            <div style={{ marginTop: 10 }}>
              <input ref={inputRef} type="file" multiple accept=".png,.jpg,.jpeg,.pdf" style={{ display: 'none' }}
                onChange={e => { enviarArquivos(e.target.files); e.target.value = ''; }}/>
              <button className="btn btn-sm btn-ghost" disabled={!!enviando} onClick={() => inputRef.current && inputRef.current.click()}>
                <Icon name="plus" size={12}/> {enviando ? `Enviando ${enviando}…` : 'Anexar arquivo'}
              </button>
              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 6 }}>
                PNG, JPG ou PDF · até 25 MB por arquivo · máximo 10 por solicitação
              </div>
            </div>
          )}

          {/* ── Histórico ── */}
          <div className="divider" style={{ marginTop: 16 }}/>
          <h4 style={{ fontSize: 13, margin: '12px 0 8px' }}>🕘 Histórico</h4>
          {(dados.historico || []).map((h, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, paddingBottom: 10, fontSize: 12 }}>
              <div style={{ width: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--navy)', flexShrink: 0, marginTop: 4 }}/>
                {i < dados.historico.length - 1 && <span style={{ flex: 1, width: 1, background: 'var(--line)' }}/>}
              </div>
              <div style={{ flex: 1 }}>
                <div>
                  {h.status_de ? <><span style={{ color: 'var(--ink-3)' }}>{h.status_de}</span> → </> : null}
                  <b>{h.status_para}</b>
                </div>
                <div style={{ color: 'var(--ink-3)', fontSize: 11 }}>{h.autor_nome} · {relativeTime(h.criado_em)}</div>
                {h.obs && <div style={{ marginTop: 3, fontStyle: 'italic' }}>💬 {h.obs}</div>}
              </div>
            </div>
          ))}

          {/* ── Ações ── */}
          {dados.podeAprovar && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
              <button className="btn btn-sm btn-danger" onClick={() => setAcao('rejeitar')}>
                <Icon name="x" size={13}/> Cancelar solicitação
              </button>
              <button className="btn btn-sm btn-primary" onClick={() => setAcao('avançar')}>
                Avançar status <Icon name="arrow-right" size={13}/>
              </button>
            </div>
          )}
        </>)}

        {acao && s && <RequestActionModal req={s} action={acao} onConfirm={confirmarAcao} onClose={() => setAcao(null)}/>}
      </div>
    </div>
  );
}


export function MyRequests({ go, mobile, user, onChange }) {
  const [filter, setFilter] = useState('Todos');
  const [modal, setModal] = useState(null);
  const [detalheId, setDetalheId] = useState(null);
  const [list, setList] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const canApprove = hasPerm(user,'servicos','approve');
  const filters = ['Todos','Pendente','Aguardando aprovação','Em andamento','Concluído','Cancelado'];
  const done = s => s==='Concluído'||s==='Cancelado';

  /* O filtro por quem-vê-o-quê saiu do cliente. Antes era
     `canApprove ? todas : filter(createdBy===user.id)` — ou seja, TODAS as
     solicitações do shopping já estavam carregadas no navegador de qualquer
     pessoa, e a tela apenas escondia o que não era dela. Agora o Worker
     devolve só o que o usuário pode ver, decidido no SQL. */
  const recarregar = async () => {
    setCarregando(true); setErroCarga('');
    try {
      const d = await API.solicitacoes.listar({ status: filter === 'Todos' ? '' : filter, limite: 100 });
      setList(d.solicitacoes || []);
    } catch (e) {
      setErroCarga(e.message || 'Não foi possível carregar as solicitações.');
    }
    setCarregando(false);
  };
  useEffect(() => { recarregar(); }, [filter]);

  const handleConfirm = async (id, status, progress, obs) => {
    try {
      await API.solicitacoes.mudarStatus(id, status, obs);
      await recarregar();
    } catch (e) {
      /* O servidor recusa transição inválida (409) e cancelamento sem motivo
         (400). A mensagem dele é mais útil do que qualquer texto genérico. */
      alert(e.message || 'Não foi possível alterar o status.');
    }
  };
  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title">
          <h2>{canApprove?'Solicitações':'Meus pedidos'}</h2>
          <p>{canApprove?'Gerencie e aprove as solicitações':'Acompanhe em tempo real'}</p>
        </div>
      </div>
      <div style={{ display:'flex',gap:8,marginBottom:16,flexWrap:'wrap' }}>
        {filters.map(f=><button key={f} className={`btn btn-sm ${filter===f?'btn-primary':'btn-ghost'}`} onClick={()=>setFilter(f)}>{f}</button>)}
      </div>
      {carregando && <div className="card" style={{ textAlign:'center',padding:32,color:'var(--ink-3)' }}>Carregando solicitações…</div>}

      {erroCarga && (
        <div className="card" style={{ padding:16,marginBottom:16,background:'var(--err-bg)',border:'1px solid #f5c2c2' }}>
          <div style={{ color:'var(--err)',fontSize:13,fontWeight:600,marginBottom:6 }}>{erroCarga}</div>
          <button className="btn btn-ghost" onClick={recarregar}>Tentar de novo</button>
        </div>
      )}

      {!carregando && !erroCarga && list.length===0 && <div className="card" style={{ textAlign:'center',padding:32,color:'var(--ink-3)' }}>Nenhuma solicitação encontrada.</div>}
      {list.map(r=>(
        <div className="req-detail-card" key={r.id}>
          <div className="req-detail-head" style={{ cursor:'pointer' }} onClick={()=>setDetalheId(r.id)} title="Abrir detalhes">
            <div style={{ flex:1,minWidth:0 }}>
              <div className="req-detail-id">{r.id}</div>
              <div className="req-detail-title">{r.title}</div>
              <div className="req-detail-meta">
                <span><Icon name="briefcase" size={11}/> {r.type}</span><span className="dot"/>
                <span><Icon name="user" size={11}/> {r.owner}</span><span className="dot"/>
                <span><Icon name="clock" size={11}/> {relativeTime(r.updated)}</span>
                {r.temPrestador && <><span className="dot"/><span title="Esta solicitação tem dados de prestador. Abra o detalhe para ver."><Icon name="user" size={11}/> prestador</span></>}
                {r.anexos > 0 && <><span className="dot"/><span title={`${r.anexos} anexo(s)`}>📎 {r.anexos}</span></>}
              </div>
            </div>
            <StatusPill status={r.status}/>
          </div>
          <div className={`progress ${r.priority==='Crítica'?'crit':r.status==='Concluído'?'ok':r.status==='Cancelado'?'crit':''}`}>
            <span style={{ width:`${r.status==='Cancelado'?100:r.progress}%`, background:r.status==='Cancelado'?'var(--err)':undefined }}/>
          </div>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:10,gap:12,flexWrap:'wrap' }}>
            <div style={{ fontSize:11,color:'var(--ink-3)' }}>
              Prioridade: <b style={{ color:r.priority==='Crítica'?'var(--err)':'var(--ink)' }}>{r.priority}</b>
              {r.obs && <span style={{ marginLeft:10,fontStyle:'italic' }}>💬 {r.obs}</span>}
            </div>
            {canApprove && !done(r.status) && (
              <div style={{ display:'flex',gap:6 }}>
                {r.status==='Aguardando aprovação' && <>
                  <button className="btn btn-sm btn-danger" onClick={()=>setModal({req:r,action:'rejeitar'})}>
                    <Icon name="x" size={13}/> Reprovar
                  </button>
                  <button className="btn btn-sm btn-primary" onClick={()=>setModal({req:r,action:'aprovar'})}
                    style={{ background:'var(--ok)',borderColor:'var(--ok)' }}>
                    <Icon name="check" size={13}/> Aprovar
                  </button>
                </>}
                {r.status!=='Aguardando aprovação' && (
                  <button className="btn btn-sm btn-ghost" onClick={()=>setModal({req:r,action:'avançar'})}>
                    Avançar <Icon name="arrow-right" size={13}/>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
      {modal && <RequestActionModal req={modal.req} action={modal.action} onConfirm={handleConfirm} onClose={()=>setModal(null)}/>}
      {detalheId && <RequestDetailModal id={detalheId} user={user} onClose={()=>setDetalheId(null)} onMudou={recarregar}/>}
    </div>
  );
}
